import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from "../../../../../../_metronic/_helpers";
import NoRecord from "../../../../../../_metronic/_partials/widgets/NoRecord";
import { BootstrapTable, Pagination, PaginationProvider, paginationFactory } from "../../../../../../_metronic/_partials/controls";
import { useDebtCollectionUIContext } from "../DebtCollectionUIContext";
import { useNavigate } from "react-router";
import moment from "moment";
import { listFranchiseDebt } from "../../../_redux/franchises/franchiseDebtCrud";
import { DebtStatusBadge } from "../DebtStatusBadge";
import {
  buildDebtListApiQuery,
  collectionMechanismLabel,
  defaultSorted,
  extractDebtListRowsAndTotal,
  formatCollectionPercentCell,
  formatDebtMoney,
  formatDebtUserRef,
  formatFranchiseNameCell,
  sizePerPageList,
} from "../DebtCollectionUIHelpers";

/** Stable React key for a debt row when the API omits `id`. */
function debtRowKey(row, index) {
  if (row.id != null) return String(row.id);
  return `debt-row-${row.franchise_id ?? "x"}-${index}`;
}

/**
 * Remote-sorted paginated table of franchise debt rows with navigation to franchise details.
 * @returns {JSX.Element} Table, pagination, and loading/error states.
 */
export function DebtCollectionTable() {
  const navigate = useNavigate();
  const {
    queryParams,
    setQueryParams,
    scopedFranchiseIdForDebtList,
  } = useDebtCollectionUIContext();

  const [rows, setRows] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const requestKey = useMemo(
    () =>
      JSON.stringify(
        buildDebtListApiQuery(queryParams, {
          scopedFranchiseId: scopedFranchiseIdForDebtList,
        }),
      ),
    [queryParams, scopedFranchiseIdForDebtList],
  );

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    const apiQuery = buildDebtListApiQuery(queryParams, {
      scopedFranchiseId: scopedFranchiseIdForDebtList,
    });
    listFranchiseDebt(apiQuery)
      .then((res) => {
        if (cancelled) return;
        const { rows: raw, total } = extractDebtListRowsAndTotal(res);
        const withKeys = raw.map((row, index) =>
          row && typeof row === "object" && row.id != null
            ? row
            : { ...row, id: debtRowKey(row, index) },
        );
        setRows(withKeys);
        setTotalCount(Number.isFinite(total) ? total : withKeys.length);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setListError(
            "Unable to load debt collection list. Please try again.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
    /* requestKey encodes queryParams for stable fetch deps */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const pageSize = Math.max(1, Number(queryParams.pageSize) || 20);
  const maxPage = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const pageNumber = Math.min(
    Math.max(1, Number(queryParams.pageNumber) || 1),
    maxPage,
  );

  useEffect(() => {
    if (queryParams.pageNumber !== pageNumber) {
      setQueryParams((p) => ({ ...p, pageNumber }));
    }
  }, [pageNumber, queryParams.pageNumber, setQueryParams]);

  const sortField = queryParams.sortField || defaultSorted[0].dataField;
  const sortOrder = queryParams.sortOrder || defaultSorted[0].order || "asc";

  const columns = useMemo(
    () => [
      {
        dataField: "unique_reference",
        text: "Unique Reference",
        sort: true,
        sortCaret,
      },
      // {
      //   dataField: "franchise_id",
      //   text: "Franchise ID",
      //   sort: true,
      //   sortCaret,
      //   formatter: (cell) => <span>{cell != null ? String(cell) : "—"}</span>,
      // },
      {
        dataField: "franchise",
        text: "Franchise",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => <span>{formatFranchiseNameCell(row)}</span>,
      },
      {
        dataField: "collection_mechanism",
        text: "Collection Mechanism",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => (
          <span>{collectionMechanismLabel(row.collection_mechanism)}</span>
        ),
      },
      {
        dataField: "collection_percent",
        text: "Collection %",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => (
          <span>{formatCollectionPercentCell(row)}</span>
        ),
      },
      {
        dataField: "status",
        text: "Status",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => <DebtStatusBadge status={row.status} />,
      },
      {
        dataField: "opening_balance",
        text: "Opening Balance",
        sort: true,
        sortCaret,
        formatter: (cell) => <span>{formatDebtMoney(cell)}</span>,
      },
      {
        dataField: "current_balance",
        text: "Current Balance",
        sort: true,
        sortCaret,
        formatter: (cell) => <span>{formatDebtMoney(cell)}</span>,
      },
      {
        dataField: "started_at",
        text: "Started At",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => {
          const d = moment(row.started_at ?? row.start_date);
          return <span>{d.isValid() ? d.format("DD/MM/YYYY") : "—"}</span>;
        },
      },
      {
        dataField: "created_by",
        text: "Created By",
        sort: true,
        sortCaret,
        formatter: (_cell, row) => (
          <span>
            {formatDebtUserRef(row.created_by) || row.created_by_display || "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList,
    sizePerPage: pageSize,
    page: pageNumber,
  };

  const handleTableChange = useCallback(
    (type, meta) => {
      if (type === "sort") {
        setQueryParams((prev) => ({
          ...prev,
          sortOrder: meta.sortOrder,
          sortField: meta.sortField,
          pageNumber: 1,
        }));
      } else {
        getHandlerTableChange(setQueryParams)(type, meta);
      }
    },
    [setQueryParams],
  );

  const rowEvents = useMemo(
    () => ({
      onClick: (_e, row) => {
        const fid = row.franchise_id;
        if (fid == null || fid === "") return;
        navigate(`/settings/franchises/franchise-details/${fid}`);
      },
    }),
    [history],
  );

  const selectRow = useMemo(
    () => ({
      mode: "checkbox",
      hideSelectAll: true,
      hideSelectColumn: true,
    }),
    [],
  );

  const pageData = rows === null ? [] : rows;
  const entitiesForWait = rows === null ? null : pageData;
  const showNoRecord =
    rows !== null && !listLoading && totalCount === 0 && !listError;

  return (
    <>
      {listError ? (
        <div
          className="alert alert-warning alert-dismissible fade show mb-3"
          role="alert"
        >
          <span>{listError}</span>
          <button
            type="button"
            className="close"
            aria-label="Dismiss"
            onClick={() => setListError(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : null}
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
            entities={pageData}
          >
            <BootstrapTable
              rowEvents={rowEvents}
              rowStyle={() => ({ cursor: "pointer" })}
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={pageData}
              columns={columns}
              selectRow={selectRow}
              defaultSorted={defaultSorted}
              sort={[
                {
                  dataField: sortField,
                  order: sortOrder,
                },
              ]}
              onTableChange={handleTableChange}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entitiesForWait} />
              <NoRecordsFoundMessage
                entities={rows === null ? null : pageData}
              />
            </BootstrapTable>

            {showNoRecord ? <NoRecord /> : null}
          </Pagination>
        )}
      </PaginationProvider>
    </>
  );
}
